/**
 * DocumentEmailModal — shared "Email {Invoice|Estimate}" modal.
 *
 * Same visual + behavioral spec as the inline modal Invoices.js has used
 * for months, extracted so Estimates (and any future doc type) can reuse
 * it without copy-paste. Behavior:
 *
 *   1. Primary CTA "Send {doc} Now" — hits `/api/invoices/send` via
 *      sendInvoiceEmail(), which uses the company's Gmail SMTP configured
 *      in Settings and attaches the auto-generated PDF.
 *   2. Secondary CTA "Download PDF & Open Email Client" — downloads the
 *      PDF locally then invokes a mailto: link so the user can paste the
 *      PDF in from their client. Fallback for users who haven't set up
 *      SMTP.
 *   3. Read-only preview of To / Subject / Body so the user knows exactly
 *      what the customer will see.
 */
import { useMemo } from 'react';
import { X, Send, Download, Mail } from 'lucide-react';
import { toast } from 'sonner';
import { sendInvoiceEmail } from '../../services/reminderService';
import { generateInvoicePDF } from '../../utils/PDFGenerator';

const DEFAULT_MSG = (docLabel) => (
  `Thank you for your business! Please find your ${docLabel.toLowerCase()} ` +
  `details below.`
);

export default function DocumentEmailModal({
  open,
  onClose,
  doc,
  docKind = 'invoice',   // 'invoice' | 'estimate'
  customer,
  companyInfo,
  onSent,                // optional callback after successful send
}) {
  const isEstimate = docKind === 'estimate';
  const docLabel = isEstimate ? 'Estimate' : 'Invoice';
  const docNumber = doc?.estimateNumber || doc?.invoiceNumber || doc?.id || '';

  // Attach doc-type marker so the shared PDF generator + preview modal
  // render the estimate variant (stamp / title / labels) when needed.
  const enrichedDoc = useMemo(() => (doc ? { ...doc, _docType: docKind } : null), [doc, docKind]);

  const emailContent = useMemo(() => {
    if (!doc) return { subject: '', body: '', customerEmail: '' };
    const total = Number(doc.total || 0).toFixed(2);
    const issue = doc.date || doc.createdAt || '';
    const due = doc.dueDate || '';
    const dueLabel = isEstimate ? 'Valid Until' : 'Due Date';
    const amountLabel = isEstimate ? 'Total Amount' : 'Total Amount';
    const brand = companyInfo?.name || 'Maidly Business Manager';
    const subject = `${docLabel} #${docNumber} from ${brand}`;
    const body = [
      `Dear ${doc.customerName || customer?.name || 'Customer'},`,
      '',
      `Thank you for your business! Please find your ${docLabel.toLowerCase()} details below:`,
      '',
      `${docLabel} Number: #${docNumber}`,
      issue ? `${docLabel} Date: ${issue}` : '',
      due ? `${dueLabel}: ${due}` : '',
      `${amountLabel}: $${total}`,
      '',
      `Please let me know if you have any questions.`,
      '',
      `Best regards,`,
      brand,
    ].filter(Boolean).join('\n');
    return {
      subject,
      body,
      customerEmail: (customer?.email || doc.customerEmail || '').trim(),
    };
  }, [doc, docKind, docLabel, docNumber, customer, companyInfo, isEstimate]);

  if (!open || !doc) return null;

  const handleSendNow = async () => {
    toast.info(`Sending ${docLabel.toLowerCase()}...`);
    let pdfBase64 = null;
    try {
      pdfBase64 = generateInvoicePDF(enrichedDoc, companyInfo, { returnBase64: true });
    } catch (e) {
      console.warn('[document-email] PDF generation failed, sending without attachment', e);
    }
    const res = await sendInvoiceEmail({
      invoice: enrichedDoc,
      customer,
      companyInfo,
      customMessage: emailContent.body,
      pdfBase64,
      docKind,
    });
    if (res.success) {
      toast.success(res.message);
      onSent && onSent(res);
      onClose();
    } else {
      toast.error(res.message);
    }
  };

  const downloadPDFAndOpenClient = () => {
    try {
      generateInvoicePDF(enrichedDoc, companyInfo);
      toast.success(`${docLabel} PDF downloaded! Please attach it to your email.`);
    } catch (e) {
      console.warn('[document-email] PDF generation failed', e);
    }
    setTimeout(() => {
      try {
        const mailtoLink = `mailto:${emailContent.customerEmail}?subject=${encodeURIComponent(emailContent.subject)}&body=${encodeURIComponent(emailContent.body)}`;
        const a = document.createElement('a');
        a.href = mailtoLink; a.style.display = 'none';
        document.body.appendChild(a); a.click();
        setTimeout(() => document.body.removeChild(a), 100);
        toast.success('Opening email client...');
      } catch (e) {
        toast.error('Failed to open email client');
      }
    }, 500);
  };

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      background: 'rgba(0, 0, 0, 0.5)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 1000, padding: '2rem',
    }}>
      <div style={{
        background: '#fff', borderRadius: 8, width: '100%',
        maxWidth: 700, maxHeight: '90vh', overflow: 'auto',
      }}>
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          padding: '1.5rem', borderBottom: '1px solid #e5e7eb',
          position: 'sticky', top: 0, background: '#fff', zIndex: 1,
        }}>
          <h2 style={{ fontSize: '1.25rem', fontWeight: 600 }}>Email {docLabel}</h2>
          <button
            onClick={onClose}
            data-testid="document-email-close"
            style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: '0.5rem' }}
            aria-label="Close modal"
          ><X size={20} /></button>
        </div>

        <div style={{ padding: '1.5rem' }}>
          {/* PDF Attachment card + primary CTAs */}
          <div style={{
            padding: '1rem',
            background: 'linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)',
            borderRadius: 8, marginBottom: '1.5rem',
            border: '1px solid #a7f3d0',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
              <div style={{
                width: 40, height: 40, background: '#10b981', borderRadius: 8,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}><Download size={20} color="white" /></div>
              <div>
                <div style={{ fontWeight: 600, color: '#065f46' }}>PDF {docLabel} Attachment</div>
                <div style={{ fontSize: 13, color: '#047857' }}>
                  {docLabel} #{docNumber} • ${(doc.total || 0).toFixed(2)}
                </div>
              </div>
            </div>
            <button
              onClick={handleSendNow}
              data-testid="document-email-send-now"
              style={{
                width: '100%', padding: '12px 16px',
                background: '#0073ea', color: '#fff', border: 'none',
                borderRadius: 8, cursor: 'pointer',
                fontWeight: 600, fontSize: 14,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                gap: 8, marginBottom: 10,
              }}
            >
              <Send size={18} /> Send {docLabel} Now (from my email)
            </button>
            <p style={{ fontSize: 12, color: '#0369a1', marginTop: -4, marginBottom: 12, textAlign: 'center' }}>
              Delivers immediately through the sender you set up in Settings, with the {docLabel.toLowerCase()} PDF attached.
            </p>

            <button
              onClick={downloadPDFAndOpenClient}
              data-testid="document-email-download-open"
              style={{
                width: '100%', padding: '12px 16px',
                background: '#10b981', color: '#fff', border: 'none',
                borderRadius: 8, cursor: 'pointer',
                fontWeight: 600, fontSize: 14,
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              }}
            >
              <Download size={18} /> Download PDF &amp; Open Email Client
            </button>
            <p style={{ fontSize: 12, color: '#047857', marginTop: 8, textAlign: 'center' }}>
              The PDF will download first, then your email client will open. Attach the downloaded PDF to your email.
            </p>
          </div>

          <div style={{
            padding: '1rem', background: '#f0f9ff', borderRadius: 6,
            marginBottom: '1.5rem', fontSize: '0.875rem', color: '#1e40af',
            display: 'flex', alignItems: 'flex-start', gap: 8,
          }}>
            <Mail size={16} style={{ marginTop: 2, flexShrink: 0 }} />
            <div><strong>Alternative:</strong> Copy the content below and paste it into your email client, then attach the PDF manually.</div>
          </div>

          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 600, marginBottom: '0.5rem' }}>To:</label>
            <div style={{
              padding: '0.75rem', background: '#f9fafb', borderRadius: 6,
              border: '1px solid #e5e7eb', fontFamily: 'monospace',
              fontSize: '0.875rem', color: '#0073ea', fontWeight: 600,
            }}>{emailContent.customerEmail || 'No email address available'}</div>
          </div>

          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 600, marginBottom: '0.5rem' }}>Subject:</label>
            <div style={{
              padding: '0.75rem', background: '#f9fafb', borderRadius: 6,
              border: '1px solid #e5e7eb', fontFamily: 'monospace', fontSize: '0.875rem',
            }}>{emailContent.subject}</div>
          </div>

          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 600, marginBottom: '0.5rem' }}>Message:</label>
            <textarea
              value={emailContent.body}
              readOnly
              style={{
                width: '100%', minHeight: 250, padding: '0.75rem',
                background: '#f9fafb', border: '1px solid #e5e7eb',
                borderRadius: 6, fontFamily: 'monospace', fontSize: '0.875rem',
                whiteSpace: 'pre-wrap', resize: 'vertical',
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

// Not-strictly-needed default export helper for DEFAULT_MSG so lint doesn't
// complain about unused local; keeps the "friendly default" available if any
// caller ever imports it.
export { DEFAULT_MSG };
